import React from 'react';
import { css, styled } from '../../../common.styles';
import { marginInline, paddingInline } from '../../../styles/general/logical-properties';
import { baseColor, mobile, primaryColor, primaryFont, secondaryColor } from '../../../styles/general/theme';
import {
  BasicButtonStyle,
  ButtonProps,
  ButtonVariant,
  SecondaryButtonWithDisabledStyle,
} from '../general/common-button';

export const CitNextChevronWrapper = styled.div<{ noMargin?: boolean }>`
  display: flex;
  margin-right: ${(p) => (p.noMargin ? 0 : 'initial')};
  margin-top: ${(p) => (p.noMargin ? 0 : 'initial')};
  margin-bottom: ${(p) => (p.noMargin ? 0 : 'initial')};
  margin-left: ${(p) => (p.noMargin ? 0 : '18px')};
`;

export const CitPrevChevronWrapper = styled.div<{ noMargin?: boolean }>`
  display: flex;
  margin-right: ${(p) => (p.noMargin ? 0 : '18px')};
  margin-top: ${(p) => (p.noMargin ? 0 : 'initial')};
  margin-bottom: ${(p) => (p.noMargin ? 0 : 'initial')};
  margin-left: ${(p) => (p.noMargin ? 0 : 'initial')};
`;

export const CitNextChevron = (props: { noMargin?: boolean; fill?: string }) => (
  <CitNextChevronWrapper noMargin={props?.noMargin}>
    <svg width="11" height="19" viewBox="0 0 11 19" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M0.180002 1.56L8.16 9.55L0.28 17.45L0.13 17.59L0.28 17.73L1.41 18.86L1.55 19L1.69 18.86L10.86 9.69L11 9.55L10.86 9.41L1.59 0.149999L1.45 -8.34888e-07L1.31 0.149999L0.180002 1.28L0.0300013 1.42L0.180002 1.56Z"
        fill={props?.fill ?? 'white'}
      />
    </svg>
  </CitNextChevronWrapper>
);

export const CitPrevChevron = (props: { noMargin?: boolean; fill?: string }) => (
  <CitPrevChevronWrapper noMargin={props?.noMargin}>
    <svg width="11" height="19" viewBox="0 0 11 19" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10.82 17.44L2.84 9.45L10.72 1.55L10.87 1.41L10.72 1.27L9.59 0.14L9.45 0L9.31 0.14L0.14 9.31L0 9.45L0.14 9.59L9.41 18.85L9.55 19L9.69 18.85L10.82 17.72L10.97 17.58L10.82 17.44Z"
        fill={props?.fill ?? '#DA291C'}
      />
    </svg>
  </CitPrevChevronWrapper>
);

export const BaseSimpleButtonStyle = css<{ disabled?: boolean | undefined }>`
  box-sizing: border-box;
  font-family: ${primaryFont};
  font-weight: 700;
  font-size: 16px;
  line-height: 17px;
  padding: 13px 15px;
  ${SecondaryButtonWithDisabledStyle};
`;

export const SimpleButtonStyle = css<{
  disabled?: boolean | undefined;
  variant?: ButtonVariant;
  width?: string;
  fullWidth?: boolean;
}>`
  ${BasicButtonStyle};
  ${BaseSimpleButtonStyle};
  text-transform: none;
  background-color: ${(p) =>
    p.disabled ? baseColor(730) : primaryColor(p.variant === 'primary' ? 200 : p.variant === 'secondary' ? 900 : 100)};
  color: ${(p) => (p.disabled ? baseColor(500) : p.variant === 'secondary' ? primaryColor(100) : baseColor(900))};
  border-radius: 24px;
  text-align: center;
  hyphens: auto;
  width: ${(p) => (p.fullWidth ? '100%' : 'fit-content')};

  ${(p) =>
    p.width &&
    css`
      width: ${p.width};
    `};

  :hover {
    background-color: ${baseColor(250)};
    color: ${baseColor(900)};

    > * {
      color: ${baseColor(900)};
    }
  }

  @media (max-width: ${mobile}px) {
    width: 100%;
  }
`;

export const SimpleButton = styled.a<{
  disabled?: boolean | undefined;
  variant?: ButtonVariant;
  darkMode?: boolean;
}>`
  ${SimpleButtonStyle};
`;

const CitArrowAndTextButtonWrapper = styled.a`
  ${BasicButtonStyle};
  ${BaseSimpleButtonStyle};
  font-family: ${primaryFont};
  margin: 0 15px;
  cursor: pointer;
  display: flex;
  text-transform: none;
  text-decoration: none;
  align-items: center;
  color: ${primaryColor(200)};
  border: 1px solid ${primaryColor(200)};
  border-radius: 24px;

  @media (max-width: ${mobile}px) {
    width: 100%;
  }
`;

const CitTextButtonWrapper = styled.a<{ darkBackground?: boolean; disabled?: boolean; darkMode?: boolean }>`
  ${BasicButtonStyle};
  ${BaseSimpleButtonStyle};
  font-family: ${primaryFont};
  margin: 0 15px;
  cursor: pointer;
  display: flex;
  text-transform: none;
  text-decoration: none;
  align-items: center;
  color: ${primaryColor(200)};
  width: fit-content;

  :hover {
    color: ${(p) => (p.darkBackground ? baseColor(900) : baseColor(100))};
  }

  ${(p) =>
    p.disabled &&
    css`
      color: ${baseColor(730)}!important;
      background-color: transparent !important;
      &:hover {
        cursor: not-allowed;
      }
    `};

  @media (max-width: ${mobile}px) {
    width: 100%;
  }

  ${(p) =>
    p.darkMode &&
    css`
      color: ${baseColor(900)};

      :hover {
        color: ${baseColor(900)};
        text-decoration: underline;
      }
    `};
`;

export const CitArrowAndTextButton: React.FC<ButtonProps> = (props) => {
  return (
    <CitArrowAndTextButtonWrapper {...props}>
      <CitBackChevron />
      {props.children}
    </CitArrowAndTextButtonWrapper>
  );
};

export const PrimaryButtonVersionAStyle = css<{ disabled?: boolean | undefined }>`
  ${SimpleButtonStyle};
  border-radius: 24px;

  background-color: ${(p) => (p.disabled ? secondaryColor(850) : primaryColor(200))};
  color: ${(p) => (p.disabled ? baseColor(725) : baseColor(900))};

  :hover {
    background-color: ${primaryColor(300)};
    color: ${baseColor(900)};

    > * {
      color: ${baseColor(900)};
    }
  }
`;

export const PrimaryButtonVersionBStyle = css<{ disabled?: boolean | undefined }>`
  ${SimpleButtonStyle};
  border-radius: 24px;

  background-color: ${(p) => (p.disabled ? secondaryColor(850) : primaryColor(100))};
  color: ${(p) => (p.disabled ? baseColor(725) : baseColor(900))};

  :hover {
    background-color: ${primaryColor(300)};
    color: ${baseColor(900)};

    > * {
      color: ${baseColor(900)};
    }
  }
`;

export const SecondaryButtonVersionAStyle = css<{ disabled?: boolean | undefined }>`
  ${SimpleButtonStyle};
  border-radius: 24px;

  background-color: transparent;
  color: ${(p) => (p.disabled ? baseColor(500) : primaryColor(200))};

  :hover {
    background-color: ${primaryColor(200)};
    color: ${baseColor(900)};

    > * {
      color: ${baseColor(900)};
    }
  }
`;

export const SecondaryButtonVersionBStyle = css<{ disabled?: boolean | undefined }>`
  ${SimpleButtonStyle};
  border-radius: 24px;

  background-color: transparent;
  color: ${(p) => (p.disabled ? baseColor(500) : primaryColor(100))};

  :hover {
    background-color: ${primaryColor(100)};
    color: ${baseColor(900)};

    > * {
      color: ${baseColor(900)};
    }
  }
`;

export const SecondarySimpleButtonStyle = css<{ disabled?: boolean | undefined }>`
  ${SimpleButtonStyle};
  background-color: ${(p) => (p.disabled ? baseColor(730) : primaryColor(100))};
  color: ${(p) => (p.disabled ? baseColor(500) : baseColor(900))};
  border-radius: 24px;

  :hover {
    background-color: ${baseColor(250)};
    color: ${baseColor(900)};

    > * {
      color: ${baseColor(900)};
    }
  }
`;

export const PrimaryBorderedSimpleButtonStyle = css<{ disabled?: boolean | undefined }>`
  ${SimpleButtonStyle};
  background-color: transparent;
  color: ${primaryColor(100)};
  border-radius: 24px;
  border: 1px solid ${primaryColor(100)};

  :hover {
    background-color: ${primaryColor(100)};
    color: ${baseColor(900)};
  }
`;

export const SecondaryBorderedSimpleButtonStyle = css<{ disabled?: boolean | undefined }>`
  ${SimpleButtonStyle};
  background-color: transparent;
  color: ${primaryColor(200)};
  border-radius: 24px;
  border: 1px solid ${primaryColor(200)};

  :hover {
    background-color: ${primaryColor(200)};
    color: ${baseColor(900)};
  }
`;

export const PrimaryButton = (props: ButtonProps) => (
  <SimpleButton
    variant={props.variant}
    onClick={props.onClick}
    href={props.href}
    className={props.className}
    {...props}
  >
    {props.children}
  </SimpleButton>
);

export const PrevButtonElementContainer = styled(SimpleButton)`
  justify-content: space-between;
`;

export const NextButtonElementContainer = styled(SimpleButton)`
  justify-content: space-between;
`;

const SecondaryButton = styled(SimpleButton)`
  ${SecondaryBorderedSimpleButtonStyle};
  pointer-events: auto;
  ${(p) =>
    p.disabled &&
    css`
      color: ${baseColor(730)}!important;
      border-color: ${baseColor(730)};
      background-color: transparent !important;
      &:hover {
        cursor: not-allowed;
      }
    `};

  ${(p) =>
    p.darkMode &&
    css`
      color: ${baseColor(900)};
      border-color: ${baseColor(900)};

      :hover {
        background-color: ${baseColor(900)};
        color: ${primaryColor(200)};
      }
    `};
`;

export const BackButtonElementContainer = styled.a<{ disabled?: boolean | undefined }>`
  ${BasicButtonStyle};
  ${BaseSimpleButtonStyle};

  text-transform: none;
  font-family: ${primaryFont};
  background-color: transparent;
  color: ${(p) => (p.disabled ? baseColor(500) : primaryColor(100))};
  height: 50px;

  box-sizing: border-box;
  padding: 13px 17px;
  line-height: unset;

  > * {
    padding-bottom: 1px;
  }

  :hover {
    background-color: ${primaryColor(100)};
    color: ${baseColor(900)};

    > * {
      color: ${baseColor(900)};
    }
  }
`;

export const WideBackButtonElementContainer = styled(BackButtonElementContainer)`
  justify-content: space-between;
`;

export const AdvisorButtonElementContainer = styled(SimpleButton)`
  background: transparent;
  border: 1px solid ${primaryColor(100)};
  color: ${primaryColor(100)};
  font-family: ${primaryFont};
  font-weight: 900;
  font-size: 14px;
  line-height: 18px;
  text-align: center;

  &:visited {
    color: ${primaryColor(100)};
  }

  &:hover {
    background: ${primaryColor(100)};
    border-width: 1px;
    color: ${baseColor(875)};

    & > * {
      color: ${baseColor(875)};
    }
  }
`;

export const ButtonElementContainer = styled(SimpleButton)<{ darkMode?: boolean }>`
  ${paddingInline(15, 10)};

  ${(p) =>
    p.darkMode &&
    css`
      color: ${primaryColor(200)};
      border-color: ${baseColor(900)};
      background-color: ${baseColor(900)};

      :hover {
        background-color: ${primaryColor(200)};
        color: ${baseColor(900)};
      }
    `};
`;

const ChevronStyle = css`
  font-size: 28px;
  font-weight: 400;
  ${marginInline(8, 0)};
  font-family: ${primaryFont};
`;

export const CitChevron = styled.div`
  color: ${(p) => p.theme.color.base[900]};
  ${paddingInline(5, 0)};
  ${ChevronStyle};

  &:after {
    content: '›';
  }
`;

const CitBackChevron = styled(CitPrevChevron)`
  color: ${(p) => p.theme.color.primary[100]};
  ${paddingInline(0, 5)};
`;

const ButtonTextNoPadding = styled.span`
  flex-grow: 1;
  display: flex;
  justify-content: center;
`;

export const CitButton = (props: ButtonProps) => {
  const variant = props.variant?.toLowerCase() as ButtonVariant;
  switch (variant) {
    case 'icon':
      return (
        <NextButtonElementContainer
          onClick={props.onClick}
          href={props.href}
          className={props.className}
          {...props}
          variant={'primary'}
        >
          {props.children}
          <CitNextChevron />
        </NextButtonElementContainer>
      );
    case 'tertiary':
      return (
        <CitTextButtonWrapper
          {...props}
          onClick={props.onClick}
          href={props.href}
          target={props.target}
          className={props.className}
        >
          {props.children}
        </CitTextButtonWrapper>
      );
    case 'secondary':
      return (
        <SecondaryButton
          {...props}
          variant={variant}
          onClick={props.onClick}
          href={props.href}
          target={props.target}
          className={props.className}
        >
          {props.children}
        </SecondaryButton>
      );
    case 'primary':
    default:
      return (
        <ButtonElementContainer
          {...props}
          variant={variant}
          onClick={props.onClick}
          href={props.href}
          target={props.target}
          className={props.className}
        >
          {props.children}
        </ButtonElementContainer>
      );
  }
};

export const CitNextButton = (props: ButtonProps) => (
  <NextButtonElementContainer
    variant={props.variant}
    onClick={props.onClick}
    href={props.href}
    className={props.className}
    {...props}
  >
    {props.children}
    <CitNextChevron />
  </NextButtonElementContainer>
);

export const CitPrevButton = (props: ButtonProps) => (
  <PrevButtonElementContainer
    variant={props.variant}
    onClick={props.onClick}
    href={props.href}
    className={props.className}
    {...props}
  >
    <CitPrevChevron />
    {props.children}
  </PrevButtonElementContainer>
);

export const CitBackButton = (props: ButtonProps) => (
  <BackButtonElementContainer onClick={props.onClick} href={props.href} className={props.className} {...props}>
    <CitBackChevron />
    {props.children}
  </BackButtonElementContainer>
);

export const CitAdvisorButton = (props: ButtonProps) => (
  <AdvisorButtonElementContainer
    variant={props.variant}
    onClick={props.onClick}
    href={props.href}
    className={props.className}
    {...props}
  >
    <ButtonTextNoPadding>{props.children}</ButtonTextNoPadding>
  </AdvisorButtonElementContainer>
);

export const CitTextButton: React.FC<ButtonProps> = (props) => {
  return <CitArrowAndTextButtonWrapper {...props}>{props.children}</CitArrowAndTextButtonWrapper>;
};

export const CitSecondaryNextButton = styled(CitButton)`
  ${SecondarySimpleButtonStyle};
`;

export const CitPrimaryBorderedButton = styled(CitButton)`
  ${PrimaryBorderedSimpleButtonStyle};
`;

export const CitSecondaryBorderedButton = styled(CitButton)`
  ${SecondaryBorderedSimpleButtonStyle};
`;

export const CitPrimaryButtonVersionA = styled(CitButton)`
  ${PrimaryButtonVersionAStyle};
`;
export const CitPrimaryButtonVersionB = styled(CitButton)`
  ${PrimaryButtonVersionBStyle};
`;
export const CitSecondaryButtonVersionA = styled(CitButton)`
  ${SecondaryButtonVersionAStyle};
`;
export const CitSecondaryButtonVersionB = styled(CitButton)`
  ${SecondaryButtonVersionBStyle};
`;
